import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Mail,
  User,
  MessageSquare,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_TYPES = [
  { value: 'pre_sales', label: 'Pre-Sales Questions' },
  { value: 'privacy', label: 'Privacy Inquiries' },
  { value: 'general_support', label: 'General Support' },
  { value: 'data_request', label: 'Data Request' },
  { value: 'abuse_report', label: 'Abuse Reports' },
  { value: 'legal', label: 'Legal Inquiries' },
  { value: 'other', label: 'Other' },
];

// Generate simple math captcha
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return {
    question: `What is ${num1} + ${num2}?`,
    answer: num1 + num2
  };
};

export default function ContactFormModal({ open, onClose }) {
  const [formData, setFormData] = useState({
    inquiry_type: '',
    other_type: '',
    full_name: '',
    email: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        inquiry_type: '',
        other_type: '',
        full_name: '',
        email: '',
        message: ''
      });
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      setSubmitted(false);
      setErrors({});
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.inquiry_type) {
      newErrors.inquiry_type = 'Please select an inquiry type';
    }
    if (formData.inquiry_type === 'other' && !formData.other_type.trim()) {
      newErrors.other_type = 'Please specify the type of inquiry';
    }
    if (!formData.full_name.trim() || formData.full_name.length < 2) {
      newErrors.full_name = 'Please enter your full name';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Please enter a message (at least 10 characters)';
    }
    if (!captchaInput || parseInt(captchaInput) !== captcha.answer) {
      newErrors.captcha = 'Incorrect answer. Please try again.';
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/public-contact/submit`, {
        inquiry_type: formData.inquiry_type,
        other_type: formData.inquiry_type === 'other' ? formData.other_type : null,
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        message: formData.message.trim(),
        captcha_answer: parseInt(captchaInput),
        captcha_expected: captcha.answer
      });
      
      setSubmitted(true);
      toast.success('Message sent successfully!');
      
      // Auto close after showing success
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(errorMsg);
      
      if (errorMsg.includes('captcha')) {
        setCaptcha(generateCaptcha());
        setCaptchaInput('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#6D4AFF]" />
            Contact Us
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-12 text-center animate-in fade-in duration-500">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-gray-400">
              We&apos;ve received your message and will respond as soon as possible.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              This window will close automatically...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Inquiry Type */}
            <div className="space-y-2">
              <Label className="text-gray-300">Inquiry Type *</Label>
              <Select
                value={formData.inquiry_type}
                onValueChange={(value) => handleChange('inquiry_type', value)}
              >
                <SelectTrigger className="bg-[#252540] border-[#2d2d44] text-white">
                  <SelectValue placeholder="Select inquiry type..." />
                </SelectTrigger>
                <SelectContent className="bg-[#252540] border-[#2d2d44] z-[200]">
                  {INQUIRY_TYPES.map((type) => (
                    <SelectItem
                      key={type.value}
                      value={type.value}
                      className="text-white hover:bg-[#6D4AFF]/20 focus:bg-[#6D4AFF]/20"
                    >
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.inquiry_type && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.inquiry_type}
                </p>
              )}
            </div>

            {/* Other Type (conditional) */}
            {formData.inquiry_type === 'other' && (
              <div className="space-y-2 animate-in slide-in-from-top duration-200">
                <Label className="text-gray-300">Please specify *</Label>
                <Input
                  value={formData.other_type}
                  onChange={(e) => handleChange('other_type', e.target.value)}
                  placeholder="Describe your inquiry type..."
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
                {errors.other_type && (
                  <p className="text-red-400 text-sm flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.other_type}
                  </p>
                )}
              </div>
            )}

            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name *
              </Label>
              <Input
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder="John Doe"
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
              {errors.full_name && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.full_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
              {errors.email && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Your Message *
              </Label>
              <Textarea
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="How can we help you?"
                className="bg-[#252540] border-[#2d2d44] text-white min-h-[120px] resize-none"
                maxLength={5000}
              />
              <div className="flex justify-between">
                {errors.message ? (
                  <p className="text-red-400 text-sm flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.message}
                  </p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-gray-500">{formData.message.length}/5000</span>
              </div>
            </div>

            {/* Captcha */}
            <div className="space-y-2 p-4 bg-[#252540] rounded-lg border border-[#2d2d44]">
              <Label className="text-gray-300 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#6D4AFF]" />
                Security Check *
              </Label>
              <div className="flex items-center gap-3">
                <div className="bg-[#1a1a2e] px-4 py-2 rounded-lg border border-[#6D4AFF]/30">
                  <span className="text-[#6D4AFF] font-mono font-bold">{captcha.question}</span>
                </div>
                <Input
                  type="number"
                  value={captchaInput}
                  onChange={(e) => {
                    setCaptchaInput(e.target.value);
                    if (errors.captcha) {
                      setErrors(prev => ({ ...prev, captcha: null }));
                    }
                  }}
                  placeholder="Answer"
                  className="bg-[#1a1a2e] border-[#2d2d44] text-white w-24"
                />
              </div>
              {errors.captcha && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.captcha}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6] hover:from-[#5B3FD9] hover:to-[#7C4FE0] text-white py-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              We typically respond within 24-48 hours.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
